import { useEffect, useRef, useState } from 'react';
import { Monitor } from 'lucide-react';
import Button from '../../ui/Button';
import { LIBRARY_NAME_MAX, libraryNameError } from '../../../utils/libraryName';
import { TONE_CLASSES } from '../../../utils/tones';

/**
 * Status line for one station, from (in priority order) the station's own
 * report, then the outcome of the last Save from this browser.
 *
 * @returns {{tone: string, text: string, detail?: string}|null}
 */
function describeStatus(entry) {
  const station = entry?.station;
  const lastSave = entry?.lastSave;

  if (station?.override?.enabled) {
    const name = station.override.libraryName || station.effective?.libraryName || 'Default';
    return { tone: 'warning', text: `Station is using its own override: ${name}` };
  }
  if (station && station.validation?.valid === false) {
    const available = (station.availableLibraries || []).join(', ');
    return {
      tone: 'error',
      text: `Library not found on station${available ? ` · available: ${available}` : ''}`,
    };
  }
  if (lastSave?.error) {
    return { tone: 'error', text: `Not saved: ${lastSave.error}` };
  }
  // The station answered but refused the setting (e.g. schema mismatch):
  // saved on TurboSync Pro, not applied on the station.
  if (lastSave?.ackStatus === 'failure') {
    return {
      tone: 'error',
      text: 'Saved, but the station rejected it',
      detail: 'The Media Station did not apply this library. It may need the latest Media Station update.',
    };
  }
  if (lastSave && !lastSave.delivered && !station) {
    return {
      tone: 'warning',
      text: 'Saved · station offline, will apply on reconnect',
      detail: 'The station is offline or needs the latest Media Station update; it will pick this up when it reconnects.',
    };
  }
  if (station?.effective) {
    return { tone: 'success', text: `Station confirmed · ${station.effective.libraryName || 'Default'}` };
  }
  if (lastSave?.delivered) {
    return { tone: 'success', text: `Station confirmed · ${lastSave.libraryName || 'Default'}` };
  }
  return null;
}

/**
 * Choose the ProPresenter library one Media Station writes presentation files
 * into. The value is saved by TurboSync Pro and pushed to the station, which
 * validates it against its own library folders.
 */
export default function StationLibraryEditor({ agent, entry, settingsStatus, onSave }) {
  const agentId = agent.agent_id;
  const stored = entry?.libraryName ?? '';
  const [value, setValue] = useState(stored);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Follow the stored value until the user starts editing.
  useEffect(() => {
    if (!dirty) setValue(stored);
  }, [stored, dirty]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const status = saving ? { tone: 'muted', text: 'Saving…' } : describeStatus(entry);
  const chips = (entry?.station?.availableLibraries || []).filter(Boolean);
  const inputId = `station-library-${agentId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const handleChange = (next) => {
    setValue(next);
    setDirty(true);
    // Validate as you type only once an error is showing, so it clears promptly.
    if (error) setError(next.trim() === '' ? null : libraryNameError(next));
  };

  const handleSave = async () => {
    // Empty means "use the station default" and is sent as null (clear).
    const name = value === '' ? null : value;
    if (name !== null) {
      const problem = libraryNameError(name);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setError(null);
    setSaving(true);
    try {
      await onSave(agentId, name);
      setDirty(false);
    } catch {
      // Outcome is recorded in context and shown in the status line.
    } finally {
      setSaving(false);
    }
  };

  const isOnline = agent.status === 'online';

  return (
    <div data-testid="station-library-editor" data-agent-id={agentId}>
      <div className="flex items-center gap-2 mb-2">
        <Monitor className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <span className="min-w-0 truncate text-body-sm font-medium text-gray-900 dark:text-gray-100">
          {agent.description || `Media Station ${agentId.slice(0, 8)}`}
        </span>
        <span className="flex-shrink-0 flex items-center gap-1.5 text-caption text-gray-500 dark:text-gray-400">
          <span
            className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`}
            aria-hidden="true"
          />
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>
      <label htmlFor={inputId} className="sr-only">
        ProPresenter library
      </label>
      <p id={hintId} className="text-caption text-gray-500 dark:text-gray-400 mb-4">
        Leave blank to use Default. The library must already exist in ProPresenter on this station.
      </p>

      {settingsStatus === 'not_configured' && (
        <p data-testid="station-library-not-configured" className="text-caption text-gray-500 dark:text-gray-400 mb-3">
          Library settings are not configured on TurboSync Pro yet. This Media Station keeps using its own library (Default unless changed on the station).
        </p>
      )}
      {settingsStatus === 'error' && (
        <p className="text-caption text-gray-500 dark:text-gray-400 mb-3">
          Couldn&apos;t load the current library setting. You can still save a library below.
        </p>
      )}

      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        noValidate
      >
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={value}
          placeholder="Default"
          maxLength={LIBRARY_NAME_MAX}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={!!error}
          aria-describedby={error ? `${errorId} ${hintId}` : hintId}
          data-testid="station-library-input"
          onChange={(e) => handleChange(e.target.value)}
          className={`flex-1 min-w-0 px-4 py-2.5 squircle-sm bg-white dark:bg-elevated-dark-2 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-gray-100 text-body focus:outline-none focus:ring-2 ${
            error ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-amber-500'
          }`}
        />
        <Button
          type="submit"
          disabled={saving}
          loading={saving}
          variant="primary"
          size="md"
          className="w-full sm:w-auto"
          data-testid="station-library-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </form>

      {error && (
        <p id={errorId} role="alert" data-testid="station-library-error" className={`text-caption mt-1.5 ${TONE_CLASSES.error}`}>
          {error}
        </p>
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" role="group" aria-label="Libraries on this Media Station">
          {chips.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => handleChange(name)}
              aria-pressed={value === name}
              data-testid="station-library-chip"
              data-library-name={name}
              className={`px-2.5 py-1 squircle-sm text-caption font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                value === name
                  ? 'bg-amber-500 text-white'
                  : 'bg-white dark:bg-elevated-dark-2 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-elevated-dark-3'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {status && (
        <div data-testid="station-library-status" role="status" aria-live="polite" className="mt-3">
          <p className={`text-body-sm ${TONE_CLASSES[status.tone]}`}>{status.text}</p>
          {status.detail && (
            <p className="text-caption text-gray-500 dark:text-gray-400 mt-0.5">{status.detail}</p>
          )}
        </div>
      )}
    </div>
  );
}
